import React, { useCallback, useState } from "react";
import { Box } from "@mui/material";
import Table, { Column } from "../../components/Table/Table";
import { Edit, Delete } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import CustomFormDialog, {
  FormField,
} from "../../components/CustomFormDialog/CustomFormDialog";
import CustomPopup from "../../components/CustomPopup/CustomPopup";
import { useTranslation } from "react-i18next";
import { clientsApi, ClientApiResponse } from "../../services/clientsApi";
import { useAlert } from "../../contexts/AlertContext";
import { useServerSidePagination } from "../../hooks/useServerSidePagination";
import { PaginatedResponse } from "../../types/api";
import { UserSelector } from "../../components/UserSelector";

/** Łączy ulicę, kod pocztowy i miasto w jeden string adresu (format: "ulica, kod miasto") */
function formatAddress(street: string, postalCode: string, city: string): string {
  const parts = [street?.trim(), [postalCode?.trim(), city?.trim()].filter(Boolean).join(" ")].filter(Boolean);
  return parts.join(", ");
}

/** Parsuje string adresu z API na { street, postalCode, city } (format: "ulica, XX-XXX miasto") */
function parseAddress(address: string | undefined): { street: string; postalCode: string; city: string } {
  if (!address || !address.trim()) {
    return { street: "", postalCode: "", city: "" };
  }
  const commaIdx = address.indexOf(", ");
  const street = commaIdx >= 0 ? address.slice(0, commaIdx).trim() : address.trim();
  const rest = commaIdx >= 0 ? address.slice(commaIdx + 2).trim() : "";
  const match = rest.match(/^(\d{2}-\d{3})\s+(.+)$/);
  const postalCode = match ? match[1] : "";
  const city = match ? match[2].trim() : rest;
  return { street, postalCode, city };
}

const Clients = () => {
  const { t } = useTranslation();
  const { showAlert } = useAlert();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<ClientApiResponse | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<ClientApiResponse | null>(null);

  // Wrapper function to convert 0-indexed page to 1-indexed for API
  const fetchClients = useCallback(
    async (
      page: number,
      pageSize: number,
      search: string
    ): Promise<PaginatedResponse<ClientApiResponse>> => {
      const response = await clientsApi.getClients({
        page: page + 1, // Convert 0-indexed to 1-indexed for API
        page_size: pageSize,
        search: search || undefined,
      });
      return response;
    },
    []
  );

  const pagination = useServerSidePagination(fetchClients, {
    initialPageSize: 10,
    debounceMs: 500,
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.detail ||
        error.message ||
        "Błąd połączenia z backendem";
      showAlert("error", `Błąd podczas pobierania klientów: ${errorMessage}`, 5000);
      console.error("Błąd podczas pobierania klientów:", error);
    },
  });

  const clientFormFields: FormField[] = [
    { name: "name", label: t("clients.form.companyName"), required: true },
    { name: "street", label: t("clients.form.street"), required: true },
    { name: "postalCode", label: t("clients.form.postalCode"), required: true },
    { name: "city", label: t("clients.form.city"), required: true },
    {
      name: "email",
      label: t("clients.form.email"),
      type: "email",
      required: true,
    },
    { name: "phone", label: t("clients.form.phone"), required: true },
    {
      name: "website",
      label: t("clients.form.website"),
      type: "url",
      required: false,
    },
    {
      name: "notes",
      label: t("clients.form.notes"),
      required: false,
      multiline: true,
      rows: 4,
    },
    {
      name: "caretaker_id",
      label: t("clients.form.caretaker"),
      required: false,
      customComponent: (props: {
        value: any;
        onChange: (value: any) => void;
        error?: boolean;
        helperText?: string;
        label: string;
        required?: boolean;
        disabled?: boolean;
      }) => (
        <UserSelector
          value={props.value || null}
          onChange={props.onChange}
          error={props.error}
          helperText={props.helperText}
          label={props.label}
          required={props.required}
        />
      ),
    },
  ];

  const columns: Column<ClientApiResponse>[] = [
    { id: "name", label: t("clients.columns.name"), flex: 1 },
    { id: "email", label: t("clients.columns.email"), flex: 1 },
    { id: "phone", label: t("clients.columns.phone"), flex: 1 },
    {
      id: "address",
      label: t("clients.columns.address"),
      flex: 1,
      format: (value: string | undefined) => value || "-",
    },
    { id: "caretaker_name", label: t("clients.columns.contactPerson"), flex: 1 },
    {
      id: "id" as keyof ClientApiResponse,
      label: t("common.actions"),
      flex: 1.2,
      align: "center",
      sortable: false,
      format: (_, row) => (
        <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleEditClick(row);
            }}
            size="small"
            color="primary"
            title={t("common.edit")}
          >
            <Edit fontSize="small" />
          </IconButton>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              setSelectedClient(row);
              setDeleteDialogOpen(true);
            }}
            size="small"
            color="primary"
            title={t("common.delete")}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  const handleSaveClient = async (formData: any) => {
    const address = formatAddress(formData.street || "", formData.postalCode || "", formData.city || "");
    try {
      if (editingClient) {
        // Update existing client
        await clientsApi.updateClient(editingClient.id, {
          name: formData.name,
          address,
          email: formData.email,
          phone: formData.phone,
          website: formData.website || undefined,
          notes: formData.notes || undefined,
          caretaker_id: formData.caretaker_id !== null && formData.caretaker_id !== undefined
            ? (typeof formData.caretaker_id === 'number' ? formData.caretaker_id : parseInt(String(formData.caretaker_id)))
            : null,
        });
        showAlert("success", t("clients.messages.updated") || "Klient zaktualizowany");
      } else {
        // Create new client
        await clientsApi.createClient({
          name: formData.name,
          address,
          email: formData.email,
          phone: formData.phone,
          website: formData.website || undefined,
          notes: formData.notes || undefined,
          caretaker_id: formData.caretaker_id !== null && formData.caretaker_id !== undefined
            ? (typeof formData.caretaker_id === 'number' ? formData.caretaker_id : parseInt(String(formData.caretaker_id)))
            : undefined,
        });
        showAlert("success", t("clients.messages.added") || "Klient został dodany");
      }
      pagination.refresh();
      setIsDialogOpen(false);
      setEditingClient(null);
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.detail ||
        error.message ||
        "Błąd podczas zapisywania klienta";
      showAlert("error", errorMessage, 5000);
      console.error("Błąd podczas zapisywania klienta:", error);
    }
  };

  const handleEditClick = (client: ClientApiResponse) => {
    setEditingClient(client);
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditingClient(null);
  };

  return (
    <Box>
      <Table
        columns={columns}
        data={pagination.data}
        page={pagination.page}
        rowsPerPage={pagination.pageSize}
        totalRows={pagination.totalCount}
        onPageChange={pagination.handlePageChange}
        onRowsPerPageChange={pagination.handlePageSizeChange}
        rowKey="id"
        title={t("clients.title")}
        description={t("clients.description")}
        onAddButtonClick={() => setIsDialogOpen(true)}
        addButtonText={t("clients.addClient")}
        searchable={true}
        searchPlaceholder={t("clients.searchPlaceholder")}
        onSearchChange={pagination.handleSearchChange}
        loading={pagination.isLoading}
      />

      <CustomFormDialog
        open={isDialogOpen}
        onClose={handleDialogClose}
        onSubmit={handleSaveClient}
        title={
          editingClient ? t("clients.editClient") : t("clients.addNewClient")
        }
        submitButtonText={
          editingClient ? t("common.saveChanges") : t("common.add")
        }
        formFields={clientFormFields}
        initialData={
          editingClient
            ? (() => {
                const { street, postalCode, city } = parseAddress(editingClient.address);
                return {
                  name: editingClient.name || "",
                  street,
                  postalCode,
                  city,
                  email: editingClient.email || "",
                  phone: editingClient.phone || "",
                  website: editingClient.website || "",
                  notes: editingClient.notes || "",
                  caretaker_id: editingClient.caretaker ?? null,
                };
              })()
            : {
                name: "",
                street: "",
                postalCode: "",
                city: "",
                email: "",
                phone: "",
                website: "",
                notes: "",
                caretaker_id: null,
              }
        }
        validate={(data: any) => {
          const errors: Record<string, string> = {};
          if (!data.name) {
            errors.name = t("clients.validation.nameRequired") || "Nazwa jest wymagana";
          }
          if (!data.street?.trim()) {
            errors.street = t("clients.validation.streetRequired") || "Ulica jest wymagana";
          }
          if (!data.postalCode?.trim()) {
            errors.postalCode = t("clients.validation.postalCodeRequired") || "Kod pocztowy jest wymagany";
          } else if (!/^\d{2}-\d{3}$/.test(String(data.postalCode).trim())) {
            errors.postalCode = t("clients.validation.invalidPostalCode") || "Kod pocztowy w formacie XX-XXX";
          }
          if (!data.city?.trim()) {
            errors.city = t("clients.validation.cityRequired") || "Miasto jest wymagane";
          }
          if (!data.email) {
            errors.email = t("clients.validation.emailRequired") || "Email jest wymagany";
          } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
            errors.email = t("clients.validation.invalidEmail") || "Nieprawidłowy format email";
          }
          if (!data.phone) {
            errors.phone = t("clients.validation.phoneRequired") || "Telefon jest wymagany";
          }
          if (data.website && data.website.trim() !== "") {
            try {
              new URL(data.website);
            } catch {
              errors.website = t("clients.validation.invalidWebsite") || "Nieprawidłowy format URL";
            }
          }
          return errors;
        }}
      />

      <CustomPopup
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title={t("clients.deleteConfirmation")}
        text={t("clients.deleteWarning")}
        confirmText={t("common.delete")}
        cancelText={t("common.cancel")}
        onConfirm={async () => {
          if (selectedClient) {
            try {
              await clientsApi.deleteClient(selectedClient.id);
              showAlert("success", t("clients.messages.deleted"));
              pagination.refresh();
            } catch (error: any) {
              const errorMessage =
                error.response?.data?.detail ||
                error.message ||
                "Błąd podczas usuwania klienta";
              showAlert("error", errorMessage, 5000);
              console.error("Błąd podczas usuwania klienta:", error);
            }
          }
          setDeleteDialogOpen(false);
          setSelectedClient(null);
        }}
      />
    </Box>
  );
};

export default Clients;
